/**
 * ProductFrame — the hero visual: one enquiry, four steps, one card.
 * @file src/web/features/main/components/ProductFrame.tsx
 *
 * ── What it shows ─────────────────────────────────────────────────────────
 * The flow the hero describes, on one inbound lead: it stalls, the follow-up
 * goes out on its own, the form is finished, the retainer is signed, the
 * documents arrive, and a matter is drafted for somebody to approve. The
 * timestamps are the argument — 23:41 to 11:41, with one human decision in
 * it, at the end.
 *
 * ── One card, four lines ──────────────────────────────────────────────────
 * Earlier versions were a product tour: three scenes, fifteen rows, a detail
 * line under every title. A hero visual gets a glance, so each step is a
 * time, a state, and nothing else. Everything it leaves out — scoring, the
 * conflict check, the document list — has room further down the page.
 *
 * ── Dark, on purpose ──────────────────────────────────────────────────────
 * It sits on the navy hero and nowhere else, so it carries its own palette
 * rather than following the reader's theme.
 *
 * ── Reduced motion ────────────────────────────────────────────────────────
 * All four steps, in place, still.
 *
 * @llm-rule WHEN: Showing the product on the marketing hero
 * @llm-rule AVOID: Adding a detail line per step — that was the complexity
 * @llm-rule AVOID: Replacing this with a screenshot or GIF
 */

import { useEffect, useState } from 'react';
import { Check, FileCheck, FolderCheck, Mail, PauseCircle, PenLine } from 'lucide-react';

/**
 * The order intake actually runs in: they stall, they are chased back, they
 * finish, they SIGN, and only then do the documents come — a firm does not
 * ask for medical records before there is a retainer. The matter is drafted
 * last, for a person to approve before it is pushed anywhere.
 *
 * A time, a state, nothing else.
 */
const STEPS = [
  { icon: PauseCircle, tone: 'warn' as const, at: '23:41', state: 'Stopped at question 6' },
  { icon: Mail, tone: 'gold' as const, at: '00:11', state: 'Follow-up sent automatically' },
  { icon: Check, tone: 'plain' as const, at: '08:02', state: 'Form finished' },
  { icon: PenLine, tone: 'gold' as const, at: '08:20', state: 'Retainer signed' },
  { icon: FolderCheck, tone: 'plain' as const, at: '11:40', state: 'Documents in · 4 of 4' },
  { icon: FileCheck, tone: 'good' as const, at: '11:41', state: 'Matter auto-drafted → human approved → Clio' },
];

const TONE = {
  warn: 'var(--f-warn)',
  gold: 'var(--f-gold)',
  good: 'var(--f-good)',
  plain: 'var(--f-muted)',
};

/* How the pass is paced. The steps land half a second apart, the finished
 * flow holds for four seconds so it can be read, and then the whole card
 * clears at once — staggered exits were what made the old version look like
 * two passes fighting over the same rows. */
const STEP_MS = 500;
const HOLD_MS = 4000;
const CLEAR_MS = 420;

const CSS = `
.mi-frame {
  --f-bg: #141F36;
  --f-head: #18243D;
  --f-line: rgba(244, 239, 230, 0.12);
  --f-fg: #F4EFE6;
  --f-muted: rgba(244, 239, 230, 0.58);
  --f-gold: #D4A72C;
  --f-good: #5FB98B;
  --f-warn: #E0A84A;
}

/* Each row animates once, as it arrives. Nothing here loops: the loop is the
 * component's own, so the exit can be one event for the whole card. */
@keyframes mi-row-in {
  from { opacity: 0; transform: translateY(7px); }
  to   { opacity: 1; transform: none; }
}
.mi-row { animation: mi-row-in .32s ease-out both; }

@media (prefers-reduced-motion: reduce) {
  .mi-row { animation: none; }
}
`;

export function ProductFrame() {
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const [shown, setShown] = useState(reduced ? STEPS.length : 0);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (reduced) return;

    /* Building. */
    if (!clearing && shown < STEPS.length) {
      const t = setTimeout(() => setShown((n) => n + 1), STEP_MS);
      return () => clearTimeout(t);
    }
    /* Complete: hold the finished flow long enough to read it. */
    if (!clearing) {
      const t = setTimeout(() => setClearing(true), HOLD_MS);
      return () => clearTimeout(t);
    }
    /* Gone, all at once — then start again from nothing. */
    const t = setTimeout(() => {
      setShown(0);
      setClearing(false);
    }, CLEAR_MS);
    return () => clearTimeout(t);
  }, [shown, clearing, reduced]);

  return (
    <div
      className="mi-frame overflow-hidden rounded-2xl border shadow-2xl"
      style={{
        background: 'var(--f-bg)',
        borderColor: 'var(--f-line)',
        color: 'var(--f-fg)',
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)',
      }}
    >
      <style>{CSS}</style>

      <div
        className="flex items-center gap-2 border-b px-4 py-2.5"
        style={{ background: 'var(--f-head)', borderColor: 'var(--f-line)' }}
      >
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="size-2.5 rounded-full" style={{ background: 'var(--f-line)' }} />
          <span className="size-2.5 rounded-full" style={{ background: 'var(--f-line)' }} />
          <span className="size-2.5 rounded-full" style={{ background: 'var(--f-line)' }} />
        </div>
        <div
          className="ml-2 flex-1 rounded px-3 py-1 text-center font-mono text-[10px]"
          style={{ background: 'rgba(0,0,0,0.25)', color: 'var(--f-muted)' }}
        >
          naumene.com
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-baseline justify-between gap-3">
          {/* Not a name. A firm reading a hero should see the shape of the
            * thing — one inbound lead, start to finish — rather than a person
            * they have to decide whether to believe in. */}
          <span className="text-sm font-medium">Inbound lead</span>
          <span className="font-mono text-[10px]" style={{ color: 'var(--f-muted)' }}>
            Personal injury · ES
          </span>
        </div>

        <ol
          className="relative mt-6 space-y-4 transition-opacity"
          style={{
            /* Reserved by the full flow, so the card never changes height as
               the steps arrive or leave. */
            minHeight: '13.5rem',
            opacity: clearing ? 0 : 1,
            transitionDuration: `${CLEAR_MS}ms`,
          }}
        >
          {/* The rail that makes six states one flow. */}
          <span
            aria-hidden="true"
            className="absolute bottom-4 left-[11px] top-3 w-px transition-opacity"
            style={{
              background: 'var(--f-line)',
              opacity: shown > 1 ? 1 : 0,
              transitionDuration: '300ms',
            }}
          />

          {STEPS.slice(0, shown).map((step) => {
            const Icon = step.icon;
            const colour = TONE[step.tone];
            return (
              <li key={step.state} className="mi-row relative flex items-center gap-3">
                <span
                  className="grid size-[23px] shrink-0 place-items-center rounded-full border"
                  style={{ borderColor: colour, color: colour, background: 'var(--f-bg)' }}
                >
                  <Icon className="size-3" aria-hidden="true" />
                </span>
                <span className="flex-1 text-[12.5px]">{step.state}</span>
                <span
                  className="shrink-0 font-mono text-[10px] tabular-nums"
                  style={{ color: 'var(--f-muted)' }}
                >
                  {step.at}
                </span>
              </li>
            );
          })}
        </ol>

        <p
          className="mt-6 border-t pt-4 text-[11px] leading-relaxed"
          style={{ borderColor: 'var(--f-line)', color: 'var(--f-muted)' }}
        >
          The firm's first touch was the approval.
        </p>
      </div>
    </div>
  );
}
